// File: src/pages/TourPage.jsx

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IoArrowBackSharp, IoClose } from 'react-icons/io5';
import { io } from 'socket.io-client';

// NEW imports for the three videos
import FindAttendVideo from '../assets/FindAttend.mp4';
import EntireFlowVideo from '../assets/EntireFlow.mp4';
import FriendChatVideo from '../assets/FriendChat.mp4';


function isPWA() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

export default function TourPage() {
  // We’ll use step=2 to mimic the original "instructions" step
  const [step, setStep] = useState(2);
  // The slides for the "tour" (copied from your original instructions step)
  const [currentSlide, setCurrentSlide] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  // Socket code remains if needed for other usage (e.g. location updates)
  const userId = localStorage.getItem('userId');
  useEffect(() => {
    const socket = io(`${process.env.REACT_APP_API_URL}`);
    socket.on('connect', () =>
      console.log('[CLIENT] TourPage socket connected =>', socket.id)
    );
    socket.on('occupancyUpdated', () => {
      // intentionally empty (no occupancy logic needed)
    });

    const timerId = setInterval(() => {
      if (navigator.geolocation && userId) {
        navigator.geolocation.getCurrentPosition((pos) => {
          const sessionId = localStorage.getItem('sessionId');
          socket.emit('locationUpdate', {
            userId,
            sessionId,
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
        });
      }
    }, 60000);

    return () => {
      clearInterval(timerId);
      socket.disconnect();
    };
  }, [userId]);

  // Slides with the three videos
  const slides = [
    {
      video: FindAttendVideo,
      title: 'Discover Events Near You',
      description: 'Find  gatherings that match your interests and connect with new people in your area',
    },
    {
      video: EntireFlowVideo,
      title: 'Attend, Meet, and Match',
      description: 'Attend IRL events, get matched, easily find each other, and enjoy fun, guided activities',
    },
    {
      video: FriendChatVideo,
      title: 'Decide If You Want to Connect',
      description: 'If it’s mutual, start chatting and get to know each other better',
    },
  ];

  const totalSlides = slides.length;
  const isLastSlide = currentSlide === totalSlides - 1;
  const current = slides[currentSlide];

  // Navigation
  function handleBack() {
    if (currentSlide === 0) {
      navigate(-1);
    } else {
      setCurrentSlide((prev) => prev - 1);
    }
  }

  // Changed to navigate('/events') so Skip / Close / Overlay closure all go to /events
  function handleSkip() {
    navigate('/events');
  }

  function handleNextOrDone() {
    if (!isLastSlide) {
      setCurrentSlide((prev) => prev + 1);
    } else {
      navigate('/events');
    }
  }

  // Renders the top bar => just the logo, "Instructions" title, and circle 'X' on the right
  function renderTopSection() {
    return (
      <header style={styles.headerContainerStyle}>
        <div style={styles.headerLogoRowStyle}>
          <div style={styles.tribeLogoStyle}>tribe</div>
        </div>
        <div style={styles.headerNavRowStyle}>
          <div style={styles.pageTitlePlainStyle}>Instructions</div>

          {/* Light grey circle with an X icon (top-right) */}
          <button style={styles.closeButtonStyle} onClick={handleSkip}>
            <IoClose size={20} />
          </button>
        </div>
      </header>
    );
  }

  // Content => same code from "instructions," but with updated "Prev" button
  function renderContent() {
    return (
      <div style={styles.stepTwoWrapper}>
        <div style={styles.fixedImageContainer}>
          {current.video ? (
            <video
              src={current.video}
              autoPlay
              loop
              muted
              playsInline
              style={{ width: '100%', height: '101%', objectFit: 'contain' }}
            />
          ) : (
            // Fallback if needed
            <div style={styles.slideImageFallback} />
          )}
        </div>

        <h3 style={styles.slideTitle}>{current.title}</h3>
        <p style={styles.slideDescription}>{current.description}</p>

        <div style={styles.paginationDots}>
          {slides.map((_, idx) => {
            const isActive = idx === currentSlide;
            return (
              <div
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                style={{
                  ...styles.dotBase,
                  ...(isActive ? styles.dotActive : {}),
                }}
              />
            );
          })}
        </div>

        <div
  style={{
    ...styles.buttonsRow,
    marginBottom: isPWA() ? '24px' : '0px', // Adjust bottom spacing for PWA compatibility
  }}
>
  <button
    style={{
      ...styles.secondaryBtn,
      ...(currentSlide === 0 ? styles.inactivePrev : {}),
    }}
    onClick={currentSlide === 0 ? undefined : handleBack}
    disabled={currentSlide === 0}
  >
    Prev
  </button>

  <button style={styles.skipBtn} onClick={handleSkip}>
    Skip
  </button>

  <button style={styles.primaryBtn} onClick={handleNextOrDone}>
    {isLastSlide ? 'Done' : 'Next'}
  </button>
</div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      {renderTopSection()}
      {renderContent()}

      <style
        dangerouslySetInnerHTML={{
          __html: `
            .hide-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
            @keyframes livePulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.3); }
              100% { transform: scale(1); }
            }
            @keyframes fadeIn {
              0% { opacity: 0; transform: translateY(10px); }
              100% { opacity: 1; transform: translateY(0); }
            }
            @keyframes fadeInTop {
              0% { opacity: 0; transform: translateY(-6px); }
              100% { opacity: 1; transform: translateY(0); }
            }
          `,
        }}
      />
    </div>
  );
}

/******************************************************
 * STYLES
 ******************************************************/
const styles = {
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    background: '#fff',
    fontFamily: "'Open Sans', sans-serif",
  },

  headerContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    borderBottom: '1px solid #eee',
  },
  headerLogoRowStyle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0.5rem',
  },
  tribeLogoStyle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#fff',
    textTransform: 'lowercase',
    backgroundColor: '#000',
    padding: '0.2rem 0.95rem',
    borderRadius: '4px',
    marginBottom: '0.15rem',
  },
  headerNavRowStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem 1rem',
    position: 'relative',
    height: '2.5rem',
  },
  pageTitlePlainStyle: {
    fontSize: '1.1rem',
    fontWeight: 650,
    color: '#000',
    textAlign: 'center',
    width: '100%',
    textTransform: 'uppercase',
    textShadow: '4px 4px 4px rgba(0,0,0,0.1)',
  },

  closeButtonStyle: {
    backgroundColor: '#f0f0f0',
    color: '#333',
    border: 'none',
    borderRadius: '50%',
    width: 34,
    height: 34,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'absolute',
    right: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
  },

  stepTwoWrapper: {
    padding: '1rem clamp(10px,5vw,20px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    animation: 'fadeIn 0.4s ease',
  },
  fixedImageContainer: {
    width: '100%',
    maxWidth: 420,
    height: 'clamp(300px, 550vw, 500px)',
    marginBottom: '1rem',
    backgroundColor: '#f0f0f0',
    borderRadius: 6,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  slideImageFallback: {
    width: '100%',
    height: '100%',
    backgroundColor: '#ddd',
  },
  slideTitle: {
    fontSize: '1.25rem',
    fontWeight: 800,
    marginBottom: 6,
    color: '#111',
    textAlign: 'center',
  },
  slideDescription: {
    fontSize: '0.9rem',
    color: '#555',
    margin: 0,
    textAlign: 'center',
    maxWidth: 420,
    lineHeight: 1.4,
    marginBottom: 16,
    padding: '0 1rem',
  },
  paginationDots: {
    display: 'flex',
    gap: '0.45rem',
    margin: '1rem 0',
    justifyContent: 'center',
  },
  dotBase: {
    height: 8,
    borderRadius: 9999,
    backgroundColor: '#ccc',
    cursor: 'pointer',
    transition: 'width 0.2s ease, background-color 0.2s ease',
    width: 8,
  },
  dotActive: {
    backgroundColor: '#007bff',
    width: 28,
  },
  buttonsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: 420,
    gap: '0.5rem',
    marginTop: isPWA() ? '24px' : '30px', // Adjust bottom spacing for PWA compatibility

  },
  secondaryBtn: {
    flex: '1 1 auto',
    backgroundColor: '#E2E8F0',
    border: 'none',
    borderRadius: 8,
    padding: '0.7rem 1.2rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#1e293b',
    cursor: 'pointer',
    transition: 'background 0.2s ease, transform 0.2s ease',
    outline: 'none',
    textAlign: 'center',
  },
  inactivePrev: {
    backgroundColor: '#f2f2f2',
    color: '#999',
    cursor: 'not-allowed',
  },
  skipBtn: {
    flex: '1 1 auto',
    backgroundColor: '#fff',
    color: '#6b7280',
    border: '2px solid #e5e7eb',
    borderRadius: 8,
    padding: '0.7rem 1.2rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s ease, transform 0.2s ease',
    outline: 'none',
    textAlign: 'center',
  },
  primaryBtn: {
    flex: '1 1 auto',
    background: 'linear-gradient(90deg, #3B82F6, #2563EB)',
    border: 'none',
    borderRadius: 8,
    padding: '0.7rem 1.2rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#fff',
    cursor: 'pointer',
    transition: 'background 0.2s ease, transform 0.2s ease',
    outline: 'none',
    textAlign: 'center',
  },
};
